import React, { ReactElement, useEffect, useState } from 'react'
import i18n from 'live/services/i18n'
import { STATIC_DATA_STATES, UnitsTypes } from '../../chart-components'
import { ModalButton, RestrictedContent, RestrictedMessage, RestrictedTitle } from '../../modal-components-style'
import { fetchEntityOptions, fetchKpiGoalsWithFilter, fetchUnitOptions, TUBULAR_API } from '../../../api/api'
import { RawActivityEvent, RestrictedPipeMovement, SelectOptions, Uom, ValueUom } from '../../../utils/type'
import { RestrictedPipeErrors, Errors, getEmptyRawRestrictedPipeEvent } from '../types'
import { KPI_COLUMN, TUBULAR_TYPE_COLUMN, PIPE_TAB_ACRONYM } from '../constants'
import { getFormattedGoalLabel } from '../../../context/utils'
import { RestrictedPipesInformation } from './PipeTab/RestrictedPipesInformation'

interface PipeTabOptionsTemplate {
    restrictedPipeMovementType: SelectOptions[]
}

interface PipeTabProps {
    restrictedPipes: RestrictedPipeMovement[]
    errors: RestrictedPipeErrors
    validateRestrictedPipeFields: (restrictedPipe: RestrictedPipeMovement) => { pass: boolean; errors: Errors }
    addRestrictedPipe: (restrictedPipe: RestrictedPipeMovement) => void
    updateCurrentRestrictedPipes: (restrictedIndex: number, key: string, newValue: any) => void
    deleteRestrictedPipe: (index: number) => void
    tubularsIntervalPerTime: boolean
    tubularType: SelectOptions | null
    assetFilterId?: string
    setIsAddRestrictedPipesButtonDisabled: (isDisabled: boolean) => void
    tubularEvent: RawActivityEvent
}

export const PipeTab = ({
    restrictedPipes,
    errors,
    validateRestrictedPipeFields,
    addRestrictedPipe,
    updateCurrentRestrictedPipes,
    deleteRestrictedPipe,
    tubularsIntervalPerTime,
    tubularType,
    assetFilterId,
    setIsAddRestrictedPipesButtonDisabled,
    tubularEvent
}: PipeTabProps): ReactElement => {
    const [lengthUnitOptions, setLengthUnitOptions] = useState<Uom[]>([])
    const [speedOptions, setSpeedOptions] = useState<ValueUom[] | null>(null)
    const [loadingSpeedOptions, setLoadingSpeedOptions] = useState(false)
    const [restrictedPipeMovementTypeList, setRestrictedPipeMovementTypeList] = useState<SelectOptions[]>([])
    const [filtersAppliedToGoals, setFiltersAppliedToGoals] = useState({ [KPI_COLUMN]: PIPE_TAB_ACRONYM })
    const [isEventStarted, setIsEventStarted] = useState(
        tubularEvent.state === STATIC_DATA_STATES.STARTED_IN_EXECUTION ||
            tubularEvent.state === STATIC_DATA_STATES.FINISHED
    )
    const [emptyRawRestrictedPipeEvent, setEmptyRawRestrictedPipeEvent] = useState(getEmptyRawRestrictedPipeEvent())
    const [isAddRestrictedPerformanceButtonDisabled, setIsAddRestrictedPerformanceButtonDisabled] = useState<boolean>(
        false
    )

    const isTubularEmpty = tubularEvent?.state === STATIC_DATA_STATES.EMPTY

    const setDefaultUomMDUnit = (defaultLengthUnit: Uom) => {
        const defaultStartMD = { ...emptyRawRestrictedPipeEvent.startMD }
        defaultStartMD.uom = defaultLengthUnit

        const defaultFinalMD = { ...emptyRawRestrictedPipeEvent.startMD }
        defaultFinalMD.uom = defaultLengthUnit

        setEmptyRawRestrictedPipeEvent(prev => ({ ...prev, startMD: defaultStartMD, finalMD: defaultFinalMD }))
    }

    const setDefaultUnits = () => {
        const defaultLengthUnit = lengthUnitOptions.find(elem => elem?.name == 'm') || lengthUnitOptions[0]

        setDefaultUomMDUnit(defaultLengthUnit)
    }

    const fetchSpeedOptions = () => {
        setLoadingSpeedOptions(true)
        fetchKpiGoalsWithFilter(filtersAppliedToGoals, assetFilterId)
            .then(result =>
                (result.data ?? []).map(goal => ({
                    value: goal.target?.goals?.value,
                    uom: { name: goal.target?.goals?.uom },
                    speedAcronym: getFormattedGoalLabel(goal) ?? ''
                }))
            )
            .then(speedOptions => {
                setSpeedOptions(speedOptions)
                setLoadingSpeedOptions(false)
            })
            .catch(err => setLoadingSpeedOptions(false))
    }

    useEffect(() => {
        fetchEntityOptions(TUBULAR_API).then((data: PipeTabOptionsTemplate) => {
            const hideUnrestrictedValue = data.restrictedPipeMovementType.filter(e => e.acronym != 'UN')
            setRestrictedPipeMovementTypeList(hideUnrestrictedValue)
        })
        fetchUnitOptions(UnitsTypes.LENGTH).then(r => {
            if (!r) return
            setLengthUnitOptions(r.options)
            const defaultLengthUnit = r.options.find(elem => elem.name == 'm') || r[0]

            setDefaultUomMDUnit(defaultLengthUnit)
        })
    }, [])

    useEffect(() => {
        setDefaultUnits()
    }, [lengthUnitOptions])

    useEffect(() => {
        setFiltersAppliedToGoals(filters => ({ ...filters, [TUBULAR_TYPE_COLUMN]: tubularType?.acronym || null }))
    }, [tubularType?.acronym])

    useEffect(() => {
        setIsAddRestrictedPipesButtonDisabled(isAddRestrictedPerformanceButtonDisabled)
    }, [isAddRestrictedPerformanceButtonDisabled])

    useEffect(() => {
        if (!restrictedPipes?.length) {
            setIsAddRestrictedPerformanceButtonDisabled(false)
        }
    }, [restrictedPipes])

    return (
        <>
            {isEventStarted && restrictedPipes?.length === 0 ? (
                <RestrictedMessage>
                    <i className="fa fa-empty-set" />
                    <RestrictedTitle>{i18n('No Restriction Applied')}</RestrictedTitle>
                    <RestrictedContent>
                        {i18n('There is no Restricted Performance defined for this tubular.')}
                    </RestrictedContent>
                </RestrictedMessage>
            ) : restrictedPipes?.length > 0 ? (
                <>
                    {restrictedPipes?.map((restrictedPipe, index) => (
                        <RestrictedPipesInformation
                            restrictedPipe={restrictedPipe}
                            index={index}
                            tubularEvent={tubularEvent}
                            deleteRestrictedPipe={deleteRestrictedPipe}
                            filtersAppliedToGoals={filtersAppliedToGoals}
                            setFiltersAppliedToGoals={setFiltersAppliedToGoals}
                            errors={errors}
                            speedOptions={speedOptions}
                            loadingSpeedOptions={loadingSpeedOptions}
                            restrictedPipeMovementTypeList={restrictedPipeMovementTypeList}
                            fetchSpeedOptions={fetchSpeedOptions}
                            updateCurrentRestrictedPipes={updateCurrentRestrictedPipes}
                            assetFilterId={assetFilterId}
                            tubularsIntervalPerTime={tubularsIntervalPerTime}
                            lengthUnitOptions={lengthUnitOptions}
                            setIsAddRestrictedPerformanceButtonDisabled={setIsAddRestrictedPerformanceButtonDisabled}
                        />
                    ))}
                </>
            ) : (
                <>
                    <RestrictedMessage>
                        <i className="fa fa-empty-set" style={{ marginTop: '34px' }} />
                        <RestrictedTitle>No Restriction Applied</RestrictedTitle>
                        <RestrictedContent>
                            There is no Restricted Performance defined for this tubular. Click the Add Restricted button
                            to include.
                        </RestrictedContent>
                    </RestrictedMessage>
                </>
            )}
            {
                <ModalButton
                    styleType="action"
                    style={{ width: '100%' }}
                    disabled={isAddRestrictedPerformanceButtonDisabled}
                    onClick={() => {
                        const defaultLengthUnit =
                            lengthUnitOptions.find(elem => elem?.name == 'm') || lengthUnitOptions[0]
                        addRestrictedPipe(getEmptyRawRestrictedPipeEvent(defaultLengthUnit))
                    }}
                >
                    <i style={{ paddingRight: '4px' }} className="far fa-plus-circle"></i>
                    {i18n('Add Restricted Pipe Movement')}
                </ModalButton>
            }
        </>
    )
}
